import { supabase } from './supabaseClient';
import { logger } from '@shared/lib/logger';

export interface ApiResponse<T> {
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  meta?: {
    timestamp: string;
    version: string;
    requestId: string;
  };
}

export interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean;
  tags?: string[]; // Cache tags for invalidation
}

export interface RequestConfig extends Omit<RequestInit, 'cache' | 'priority'> {
  cache?: CacheConfig;
  timeout?: number;
  retries?: number;
  priority?: 'low' | 'normal' | 'high';
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    stack?: string;
  };
}

export class ApiClientError extends Error {
  status: number;
  code?: string;
  details?: Record<string, any>;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, any>
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

class ApiClient {
  private baseUrl: string;
  private tokenRefreshPromise: Promise<void> | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private requestCache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private requestQueue: Array<{ request: () => Promise<any>; priority: 'low' | 'normal' | 'high' }> = [];
  private isProcessingQueue = false;
  private enableCustomHeaders = false;
  private rateLimitState = {
    requests: 0,
    resetTime: Date.now() + 60000, // Reset every minute
    maxRequests: 1000, // Increased from 100 to 1000 requests per minute
  };

  constructor() {
    const rawUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

    // Normalize base URL to always target /api/v1 without duplicates or missing segments
    const trimTrailingSlash = (value: string) => value.replace(/\/+$/g, '');
    const urlNoTrail = trimTrailingSlash(rawUrl);

    let normalizedBase = urlNoTrail;
    const hasApi = /\/(api)(\/|$)/.test(urlNoTrail);
    const endsWithV1 = /\/v1$/.test(urlNoTrail);
    const apiSegmentRegex = /\/api(?:\/[^?#]*)*(?:[?#].*)?$/;

    if (endsWithV1) {
      // Already points to .../v1
      normalizedBase = urlNoTrail;
    } else if (hasApi && apiSegmentRegex.test(urlNoTrail)) {
      // Has /api already; replace the entire /api/* segment with /api/v1 to avoid duplicating paths
      normalizedBase = urlNoTrail.replace(apiSegmentRegex, "/api/v1");
    } else {
      // Missing /api; append /api/v1
      normalizedBase = urlNoTrail + '/api/v1';
    }

    this.baseUrl = normalizedBase;
    
    console.log('API Client initialized with base URL:', this.baseUrl);
    console.log('Environment VITE_API_URL:', import.meta.env.VITE_API_URL);
    
    // Enable custom headers only if explicitly configured
    this.enableCustomHeaders = import.meta.env.VITE_ENABLE_CUSTOM_HEADERS === 'true';
    
    // Start cache cleanup interval
    this.startCacheCleanup();
    
    // Start request queue processor
    this.processRequestQueue();
  }

  private async getAuthToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    // If a refresh is already in progress, wait for it
    if (this.tokenRefreshPromise) {
      await this.tokenRefreshPromise;
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (session && session.expires_at) {
      const expiresAt = session.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const tenMinutes = 10 * 60 * 1000; // Increased buffer to 10 minutes
      
      // Refresh if token expires in less than 10 minutes
      if (expiresAt - now < tenMinutes) {
        logger.debug('Token refresh needed, refreshing');
        
        this.tokenRefreshPromise = supabase.auth.refreshSession().then(({ error }) => {
          if (error) {
            logger.error('Token refresh failed', error);
            throw error;
          }
          logger.debug('Token refreshed successfully');
        }).finally(() => {
          this.tokenRefreshPromise = null;
        });
        
        await this.tokenRefreshPromise;
      }
    }
  }

  private startCacheCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.requestCache.entries()) {
        if (now > entry.timestamp + entry.ttl) {
          this.requestCache.delete(key);
        }
      }
    }, 60000); // Clean up every minute
  }

  private async processRequestQueue(): Promise<void> {
    if (this.isProcessingQueue) return;
    
    this.isProcessingQueue = true;
    
    while (this.requestQueue.length > 0) {
      // Check rate limit
      if (!this.checkRateLimit()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }
      
      // Sort by priority (high -> normal -> low)
      this.requestQueue.sort((a, b) => {
        const priorityOrder = { high: 3, normal: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });
      
      const queuedItem = this.requestQueue.shift();
      if (!queuedItem) {
        continue;
      }

      const { request } = queuedItem;
      
      try {
        await request();
      } catch (error) {
        logger.error('Queued request failed', error);
      }
      
      // Small delay between requests to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.isProcessingQueue = false;
  }

  private checkRateLimit(): boolean {
    const now = Date.now();
    
    // Reset rate limit if time window has passed
    if (now > this.rateLimitState.resetTime) {
      this.rateLimitState.requests = 0;
      this.rateLimitState.resetTime = now + 60000;
    }
    
    return this.rateLimitState.requests < this.rateLimitState.maxRequests;
  }

  private getCacheKey(endpoint: string, options: RequestInit): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${endpoint}:${body}`;
  }

  private getFromCache(cacheKey: string): any | null {
    const entry = this.requestCache.get(cacheKey);
    if (!entry) return null;
    
    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      this.requestCache.delete(cacheKey);
      return null;
    }
    
    return entry.data;
  }

  private setCache(cacheKey: string, data: any, ttl: number): void {
    this.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  public invalidateCache(pattern?: string): void {
    if (!pattern) {
      this.requestCache.clear();
      return;
    }
    
    for (const key of this.requestCache.keys()) {
      if (key.includes(pattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * Configure whether to send custom headers (X-Request-ID, X-Client-Version)
   * Disable this if you encounter CORS issues
   */
  public setCustomHeadersEnabled(enabled: boolean): void {
    this.enableCustomHeaders = enabled;
  }

  /**
   * Get current custom headers configuration
   */
  public isCustomHeadersEnabled(): boolean {
    return this.enableCustomHeaders;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestConfig = {},
    retryCount = 0
  ): Promise<T> {
    const {
      cache,
      timeout = 30000,
      retries: _retries = 3,
      priority: _priority = 'normal',
      ...requestOptions
    } = options;
    
    // Create cache key
    const cacheKey = this.getCacheKey(endpoint, requestOptions);
    
    // Check cache first for GET requests
    if (requestOptions.method === 'GET' || !requestOptions.method) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        logger.debug('Returning cached data', { endpoint, cacheKey });
        return cachedData;
      }
    }
    
    // Create a unique key for this request to handle deduplication
    const requestKey = `${requestOptions.method || 'GET'}-${endpoint}-${JSON.stringify(requestOptions.body || '')}`;
    
    // If the same request is already in progress, wait for it
    const pendingRequest = this.pendingRequests.get(requestKey);
    if (pendingRequest) {
      logger.debug('Deduplicating concurrent request', { requestKey });
      return pendingRequest;
    }

    const requestPromise = this.executeRequest<T>(endpoint, requestOptions, retryCount, timeout);
    
    // Store the promise to handle concurrent requests
    this.pendingRequests.set(requestKey, requestPromise);
    
    try {
      const result = await requestPromise;
      
      // Cache GET requests if cache config is provided
      if ((requestOptions.method === 'GET' || !requestOptions.method) && cache?.ttl) {
        this.setCache(cacheKey, result, cache.ttl);
      }
      
      return result;
    } finally {
      // Clean up the pending request
      this.pendingRequests.delete(requestKey);
    }
  }

  private async executeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0,
    timeout = 30000
  ): Promise<T> {
    const startTime = Date.now();
    
    try {
      // Check rate limit
      if (!this.checkRateLimit()) {
        throw new ApiClientError(
          'Rate limit exceeded. Please try again later.',
          429,
          'RATE_LIMIT_EXCEEDED'
        );
      }
      
      // Increment rate limit counter
      this.rateLimitState.requests++;
      
      // Ensure token is fresh
      await this.refreshTokenIfNeeded();
      
      const token = await this.getAuthToken();
      
      const url = `${this.baseUrl}${endpoint}`;
      console.log('Making request to:', url);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // Only add custom headers if explicitly enabled (to avoid CORS issues)
      if (this.enableCustomHeaders) {
        headers['X-Request-ID'] = crypto.randomUUID();
        headers['X-Client-Version'] = '1.0.0';
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const config: RequestInit = {
        ...options,
        headers,
        signal: AbortSignal.timeout(timeout),
      };

      const response = await fetch(url, config);
      
      // Log performance metrics
      const duration = Date.now() - startTime;
      logger.performanceMetric('api_request_duration', duration, 'ms');
      
      if (!response.ok) {
        await this.handleErrorResponse(response, endpoint, options, retryCount);
      }

      // Handle empty responses (like 204 No Content)
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();
      
      // Log successful request
      logger.debug('API request successful', {
        endpoint,
        method: options.method || 'GET',
        duration,
        status: response.status,
      });
      
      return data;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      if (error instanceof ApiClientError) {
        logger.error('API request failed', {
          endpoint,
          method: options.method || 'GET',
          duration,
          error: error.message,
          status: error.status,
          code: error.code,
        });
        throw error;
      }
      
      // Handle timeout errors
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        logger.error('API request timeout', { endpoint, timeout, duration });
        throw new ApiClientError(
          `Request timeout after ${timeout}ms`,
          408,
          'REQUEST_TIMEOUT'
        );
      }
      
      // Handle abort errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        logger.error('API request aborted', { endpoint, duration });
        throw new ApiClientError(
          'Request was aborted',
          0,
          'REQUEST_ABORTED'
        );
      }
      
      // Network or other errors
      logger.error('Network error', { endpoint, duration, error });
      throw new ApiClientError(
        'Network error or server unavailable',
        0,
        'NETWORK_ERROR'
      );
    }
  }

  private async handleErrorResponse(
    response: Response,
    endpoint: string,
    options: RequestInit,
    retryCount: number
  ): Promise<never> {
    let errorData: ApiError;
    
    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, create a generic one
      errorData = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        },
      };
    }

    const { error } = errorData;
    
    // Handle rate limit errors with exponential backoff
    if (response.status === 429 && retryCount < 3) {
      // Calculate exponential backoff: 1s, 2s, 4s
      const backoffMs = Math.pow(2, retryCount) * 1000;
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : backoffMs;
      
      logger.warn('Rate limit exceeded, retrying with backoff', {
        endpoint,
        retryCount: retryCount + 1,
        waitTimeMs: waitTime,
      });
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Retry the request
      return await this.executeRequest(endpoint, options, retryCount + 1);
    }
    
    // Handle authentication errors by attempting token refresh and retry
    if (response.status === 401 && retryCount < 1) {
      try {
        logger.info('401 error, attempting token refresh and retry', { endpoint });
        
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !data.session) {
          logger.error('Token refresh failed', refreshError);
          throw new ApiClientError(
            'Authentication required. Please sign in again.',
            401,
            'AUTHENTICATION_REQUIRED'
          );
        }
        
        logger.debug('Token refreshed, retrying request', { endpoint });
        
        // Retry the request with the new token
        return await this.executeRequest(endpoint, options, retryCount + 1);
        
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw error;
        }
        
        throw new ApiClientError(
          'Authentication required. Please sign in again.',
          401,
          'AUTHENTICATION_REQUIRED'
        );
      }
    }

    throw new ApiClientError(
      error.message || 'An error occurred',
      response.status,
      error.code,
      error.details
    );
  }

  // HTTP Methods
  async get<T>(
    endpoint: string, 
    params?: Record<string, any>, 
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<T> {
    let finalEndpoint = endpoint;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        finalEndpoint += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
    }

    return this.makeRequest<T>(finalEndpoint, {
      method: 'GET',
      ...config,
    });
  }

  async post<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async patch<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async put<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async delete<T>(
    endpoint: string, 
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE',
      ...config,
    });
  }

  // Batch request method for multiple API calls
  async batch(requests: Array<{
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data?: any;
    params?: Record<string, any>;
    config?: RequestConfig;
  }>): Promise<any[]> {
    const promises = requests.map(({ endpoint, method = 'GET', data, params, config }) => {
      switch (method) {
        case 'GET':
          return this.get(endpoint, params, config);
        case 'POST':
          return this.post(endpoint, data, config);
        case 'PUT':
          return this.put(endpoint, data, config);
        case 'PATCH':
          return this.patch(endpoint, data, config);
        case 'DELETE':
          return this.delete(endpoint, config);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    });

    return Promise.all(promises);
  }

  // Optimistic update helper
  async optimisticUpdate<T>(
    endpoint: string,
    data: any,
    rollbackFn?: () => void
  ): Promise<T> {
    try {
      const result = await this.put<T>(endpoint, data);
      return result;
    } catch (error) {
      // Rollback on error
      if (rollbackFn) {
        rollbackFn();
      }
      throw error;
    }
  }

  // File upload helper (for presigned URLs)
  async uploadFile(presignedUrl: string, file: File | Blob, contentType: string): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new ApiClientError(
        'File upload failed',
        response.status,
        'UPLOAD_FAILED'
      );
    }
  }
}

export const apiClient = new ApiClient();